import { createContext, useContext, useEffect } from "react";

/**
 * A record page's own title for the shell's title bar.
 *
 * The bar takes its title from the active nav item, which on a record's page
 * is the generic "تفاصيل المتجر" — the admin could not tell WHICH store they
 * were on. A page that knows its record calls `usePageTitle` with the record's
 * name (and an optional subtitle and LTR token, e.g. the owner and their
 * phone); the shell shows that instead and goes back to the nav label when the
 * page unmounts.
 *
 * Primitive arguments only, so the effect below re-runs when a value changes
 * and never on a re-render — no memoisation needed at the call site.
 */
export type PageTitleOverride = {
  title: string;
  subtitle?: string;
  /** A phone, code or email shown after the subtitle as an LTR isolate. */
  subtitleLtr?: string;
};

export const PageTitleContext = createContext<
  ((value: PageTitleOverride | null) => void) | null
>(null);

export function usePageTitle(
  title: string | undefined,
  subtitle?: string,
  subtitleLtr?: string,
) {
  const setTitle = useContext(PageTitleContext);
  useEffect(() => {
    if (!setTitle || !title) return;
    setTitle({ title, subtitle, subtitleLtr });
    return () => setTitle(null);
  }, [setTitle, title, subtitle, subtitleLtr]);
}
